import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Gamepad2, Eye, Clock, Play } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { CoverageContent } from "@/components/CoverageContent";
import { recalculateCreatorBadges } from "@/services/pointsService";
import { updateCoverContent } from "@/lib/trustScoreUtils";

interface ContentItem {
  id: string;
  title: string;
  thumbnail_url: string;
  url: string;
  platform: 'youtube' | 'twitch';
  published_at: string;
  view_count?: number;
  duration?: string;
  tags?: string[];
}

interface UserProfile {
  twitch_connected: boolean;
  youtube_connected: boolean;
  twitch_username?: string;
  youtube_username?: string;
}

interface ApprovedGame {
  id: string;
  title: string;
  hashtag: string;
  key_request_id: string;
  game_id: string;
}

interface CoverageItem {
  id: string;
  creator_id: string;
  game_id: string;
  key_request_id: string;
  title: string;
  thumbnail_url: string;
  video_url: string;
  platform: 'youtube' | 'twitch';
  posted_date: string;
  views: number;
  tags: string[];
  created_at: string;
  game?: {
    id: string;
    title: string;
    cover_image_url?: string;
  };
  creator?: {
    id: string;
    display_name: string;
    studio_name?: string;
    avatar_url?: string;
  };
}

interface DeveloperGame {
  id: string;
  title: string;
  cover_image_url?: string;
}

function Coverage() {
  const { user } = useAuth();
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [approvedGames, setApprovedGames] = useState<ApprovedGame[]>([]);
  const [coverageItems, setCoverageItems] = useState<CoverageItem[]>([]);
  const [userType, setUserType] = useState<'creator' | 'developer' | 'player' | null>(null);
  const [developerGames, setDeveloperGames] = useState<DeveloperGame[]>([]);
  const [selectedGameId, setSelectedGameId] = useState<string>('all');
  
  // Convert ContentItem to CoverageItem for CoverageContent component
  const getDisplayCoverageItems = (): CoverageItem[] => {
    if (userType === 'creator') {
      return coverageItems;
    } else if (userType === 'developer') {
      if (selectedGameId === 'all') {
        return coverageItems;
      }
      return coverageItems.filter(item => item.game_id === selectedGameId);
    }
    return [];
  };

  useEffect(() => {
    if (user?.id) {
      fetchUserProfile();
    }
  }, [user?.id]);

  useEffect(() => {
    if (userProfile && (userProfile.youtube_connected || userProfile.twitch_connected)) {
      fetchContent();
    }
  }, [userProfile]);

  useEffect(() => {
    if (user?.id && userType === 'creator') {
      fetchApprovedGames();
    }
  }, [user?.id, userType]);

  useEffect(() => {
    if (user?.id && userType === 'developer' && developerGames.length > 0) {
      fetchCoverageItems();
    }
  }, [user?.id, userType, developerGames]);

  const fetchUserProfile = async () => {
    if (!user) return;

    try {
      setIsProfileLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('twitch_connected, youtube_connected, twitch_username, youtube_username, user_type')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        return;
      }

      setUserProfile(data as any);
      setUserType((data as any).user_type as 'creator' | 'developer' | 'player');
      
      // Si c'est un développeur, récupérer ses jeux puis les coverages
      if ((data as any).user_type === 'developer') {
        await fetchDeveloperGames();
        // fetchCoverageItems sera appelé automatiquement via useEffect quand developerGames sera chargé
      } else if ((data as any).user_type === 'creator') {
        // Pour les créateurs, récupérer directement les coverages
        await fetchCoverageItems();
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setIsProfileLoading(false);
    }
  };

  const fetchApprovedGames = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("key_requests")
        .select(`
          id,
          game_id,
          game:games(
            id,
            title,
            hashtag
          )
        `)
        .eq("creator_id", user.id)
        .eq("status", "approved");

      if (error) {
        console.error('Error fetching approved games:', error);
        return;
      }

      const games = (data as any)?.map((item: any) => ({
        ...item.game,
        key_request_id: item.id,
        game_id: item.game_id
      })).filter(Boolean) || [];
      
      setApprovedGames(games);
    } catch (error) {
      console.error('Error fetching approved games:', error);
    }
  };

  const fetchDeveloperGames = async () => {
    if (!user) return;

    try {
      const { data, error } = await (supabase as any)
        .from("games")
        .select("id, title, cover_image_url")
        .eq("developer_id", user.id)
        .eq("is_published", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error('Error fetching developer games:', error);
        return;
      }

      setDeveloperGames(data || []);
    } catch (error) {
      console.error('Error fetching developer games:', error);
    }
  };

  const fetchCoverageItems = async () => {
    if (!user) return;

    try {
      
      let query = (supabase as any)
        .from("coverage")
        .select("*")
        .order("posted_date", { ascending: false });

      // Pour les créateurs, récupérer seulement leurs coverages
      if (userType === 'creator') {
        query = query.eq("creator_id", user.id);
      }
      // Pour les développeurs, récupérer les coverages de leurs jeux
      else if (userType === 'developer' && developerGames.length > 0) {
        const gameIds = developerGames.map(game => game.id);
        query = query.in("game_id", gameIds);
      }

      const { data, error } = await (query as any);

      if (error) {
        console.error('Error fetching coverage items:', error);
        return;
      }

      
      // Si on a des données, récupérer les infos des créateurs séparément
      if (data && data.length > 0) {
        const creatorIds = [...new Set((data as any[]).map((item: any) => item.creator_id))] as string[];
        
        const { data: creatorsData, error: creatorsError } = await supabase
          .from("profiles")
          .select("id, display_name, studio_name, avatar_url")
          .in("id", creatorIds);
          
        if (creatorsError) {
          console.error('Error fetching creators:', creatorsError);
        } else {
          // Enrichir les données avec les infos des créateurs
          const enrichedData = (data as any[]).map((item: any) => ({
            ...item,
            creator: creatorsData?.find((creator: any) => creator.id === item.creator_id)
          }));
          
          setCoverageItems(enrichedData as CoverageItem[]);
          return;
        }
      }
      
      setCoverageItems((data || []) as CoverageItem[]);
    } catch (error) {
      console.error('Error fetching coverage items:', error);
    }
  };

  const fetchContent = async () => {
    if (!user || !userProfile) {
      return;
    }

    setIsContentLoading(true);
    const allContent: ContentItem[] = [];

    try {
      // Fetch YouTube content if connected
      if (userProfile.youtube_connected && import.meta.env.VITE_YOUTUBE_API_KEY) {
        const youtubeContent = await fetchYouTubeContent(import.meta.env.VITE_YOUTUBE_API_KEY, userProfile);
        allContent.push(...youtubeContent);
      }

      // Fetch Twitch content if connected
      if (userProfile.twitch_connected && import.meta.env.VITE_TWITCH_CLIENT_ID) {
        const twitchContent = await fetchTwitchContent(import.meta.env.VITE_TWITCH_CLIENT_ID, userProfile);
        allContent.push(...twitchContent);
      }

      // Sort by published date (newest first)
      allContent.sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime());

      // Save matching content to coverage table and update is_covered
      await saveMatchingContent(allContent);
      
      setContentItems(allContent);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to fetch content');
    } finally {
      setIsContentLoading(false);
    }
  };

  const fetchYouTubeContent = async (apiKey: string, userProfile: UserProfile): Promise<ContentItem[]> => {
    try {
      // 1. Récupérer l'ID de la chaîne via le username
      const channelResponse = await fetch(`https://www.googleapis.com/youtube/v3/channels?part=contentDetails,snippet&forUsername=${userProfile?.youtube_username}&key=${apiKey}`, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!channelResponse.ok) {
        const errorText = await channelResponse.text();
        console.error('Failed to fetch YouTube channel:', channelResponse.status, errorText);
        return [];
      }

      const channelData = await channelResponse.json();
      if (!channelData.items || channelData.items.length === 0) {
        console.error('No YouTube channel found for username:', userProfile?.youtube_username);
        return [];
      }

      const uploadsPlaylistId = channelData.items[0].contentDetails.relatedPlaylists.uploads;
      const channelTitle = channelData.items[0].snippet?.title || 'Unknown Channel';

      // 2. Lister les vidéos de la playlist uploads
      const playlistResponse = await fetch(`https://www.googleapis.com/youtube/v3/playlistItems?part=snippet,contentDetails&playlistId=${uploadsPlaylistId}&maxResults=10&key=${apiKey}`, {
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!playlistResponse.ok) {
        console.error('Failed to fetch YouTube playlist items:', playlistResponse.status);
        return [];
      }

      const playlistData = await playlistResponse.json();
      if (!playlistData.items || playlistData.items.length === 0) {
        return [];
      }

      // 3. Récupérer les statistiques et métadonnées des vidéos
      const videoIds = playlistData.items.map((item: any) => item.contentDetails.videoId).join(',');
      const videosResponse = await fetch(`https://www.googleapis.com/youtube/v3/videos?part=statistics,snippet,contentDetails&id=${videoIds}&key=${apiKey}`, {
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!videosResponse.ok) {
        console.error('Failed to fetch YouTube video details:', videosResponse.status);
        return [];
      }

      const videosData = await videosResponse.json();

      return videosData.items.map((video: any) => ({
        id: video.id,
        title: video.snippet.title,
        thumbnail_url: video.snippet.thumbnails.maxres?.url || video.snippet.thumbnails.high?.url || video.snippet.thumbnails.default?.url,
        url: `https://www.youtube.com/watch?v=${video.id}`,
        platform: 'youtube' as const,
        published_at: video.snippet.publishedAt,
        view_count: parseInt(video.statistics.viewCount || '0'),
        duration: video.contentDetails.duration,
        tags: video.snippet.tags || []
      }));
    } catch (error) {
      console.error('Error fetching YouTube content:', error);
      return [];
    }
  };

  const fetchTwitchContent = async (clientId: string, userProfile: UserProfile): Promise<ContentItem[]> => {
    try {
      // 1. Obtenir un App Access Token
      const tokenResponse = await fetch('https://id.twitch.tv/oauth2/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `client_id=${clientId}&client_secret=${import.meta.env.VITE_TWITCH_CLIENT_SECRET}&grant_type=client_credentials`
      });

      if (!tokenResponse.ok) {
        console.error('Failed to get Twitch app access token:', tokenResponse.status);
        return [];
      }

      const tokenData = await tokenResponse.json();
      const appAccessToken = tokenData.access_token;

      // 2. Récupérer l'user_id depuis le login (pseudo) - utiliser le username directement
      const userResponse = await fetch(`https://api.twitch.tv/helix/users?login=${userProfile.twitch_username}`, {
        headers: {
          'Authorization': `Bearer ${appAccessToken}`,
          'Client-Id': clientId,
          'Accept': 'application/json'
        }
      });

      
      if (!userResponse.ok) {
        const errorText = await userResponse.text();
        console.error('Failed to fetch Twitch user:', userResponse.status, errorText);
        return [];
      }

      const userData = await userResponse.json();
      if (!userData.data || userData.data.length === 0) {
        console.error('No Twitch user found');
        return [];
      }

      const userId = userData.data[0].id; // data[0].id = user_id
      const username = userData.data[0].display_name;

      // 3. Récupérer les VODs (videos) d'un utilisateur
      const videosResponse = await fetch(`https://api.twitch.tv/helix/videos?user_id=${userId}&first=10&type=archive`, {
        headers: {
          'Authorization': `Bearer ${appAccessToken}`,
          'Client-Id': clientId,
          'Accept': 'application/json'
        }
      });

      if (!videosResponse.ok) {
        console.error('Failed to fetch Twitch videos:', videosResponse.status);
        return [];
      }

      const videosData = await videosResponse.json();

      return videosData.data.map((video: any) => ({
        id: video.id,
        title: video.title,
        thumbnail_url: video.thumbnail_url.replace('{width}', '320').replace('{height}', '180'),
        url: video.url,
        platform: 'twitch' as const,
        published_at: video.created_at,
        view_count: video.view_count,
        duration: video.duration
      }));
    } catch (error) {
      console.error('Error fetching Twitch content:', error);
      return [];
    }
  };


  const saveMatchingContent = async (contentItems: ContentItem[]) => {
    if (!user || !approvedGames.length) return;

    try {
      const matchingContent = contentItems.filter(item => {
        if (item.platform === 'youtube' && item.tags) {
          return hasMatchingApprovedGame(item.tags);
        }
        
        if (item.platform === 'twitch') {
          // Pour Twitch, on va chercher dans le titre et la description
          const title = item.title.toLowerCase();
          return approvedGames.some(game => {
            if (!game.hashtag) return false;
            const gameHashtag = game.hashtag.replace('#', '').toLowerCase();
            return title.includes(gameHashtag) || gameHashtag.includes(title);
          });
        }
        
        return false;
      });

      for (const content of matchingContent) {
        // Trouver le jeu correspondant dans les jeux approuvés du créateur
        const matchingGame = approvedGames.find((game: any) => {
          if (!game.hashtag) return false;
          const gameHashtag = game.hashtag.replace('#', '').toLowerCase();
          
          if (content.platform === 'youtube' && content.tags) {
            return content.tags.some(tag => {
              const cleanTag = tag.toLowerCase();
              return cleanTag.includes(gameHashtag) || gameHashtag.includes(cleanTag);
            });
          }
          
          if (content.platform === 'twitch') {
            const title = content.title.toLowerCase();
            return title.includes(gameHashtag) || gameHashtag.includes(title);
          }
          
          return false;
        });

        if (!matchingGame) continue;

        // Vérifier que le créateur a bien une key_request approuvée pour ce jeu
        const { data: keyRequest, error: keyRequestError } = await supabase
          .from("key_requests")
          .select("id, status")
          .eq("creator_id", user.id)
          .eq("game_id", (matchingGame as any).game_id)
          .eq("status", "approved")
          .single();

        if (keyRequestError || !keyRequest) {
          continue;
        }

        // Vérifier si ce contenu n'est pas déjà sauvegardé
        const { data: existingCoverage } = await (supabase as any)
          .from("coverage")
          .select("id")
          .eq("creator_id", user.id)
          .eq("video_url", content.url)
          .single();

        if (existingCoverage) {
          // Même si le contenu existe déjà, on met à jour is_covered
          const { error: updateError } = await (supabase as any)
            .from("key_requests")
            .update({ is_covered: true })
            .eq("id", keyRequest.id);

          if (updateError) {
            console.error('Error updating is_covered for existing content:', updateError);
          }
          continue;
        }

        // Sauvegarder dans la table coverage
        const { error: coverageError } = await (supabase as any)
          .from("coverage")
          .insert({
            creator_id: user.id,
            game_id: (matchingGame as any).game_id,
            key_request_id: keyRequest.id,
            title: content.title,
            thumbnail_url: content.thumbnail_url,
            video_url: content.url,
            platform: content.platform,
            posted_date: content.published_at,
            views: content.view_count || 0,
            tags: content.tags || []
          });

        if (coverageError) {
          console.error('Error saving coverage:', coverageError);
          continue;
        }

        // Mettre à jour le compteur cover_content
        try {
          await updateCoverContent(user.id, true);
          
          // Recalculer les badges après mise à jour du compteur
          if (user?.id && userType === 'creator') {
            recalculateCreatorBadges(user.id).catch(error => {
              console.error('Error recalculating creator badges after content creation:', error);
            });
          }
        } catch (error) {
          console.error('Error updating cover_content:', error);
        }


        // Mettre à jour is_covered dans key_requests
        const { error: updateError } = await (supabase as any)
          .from("key_requests")
          .update({ is_covered: true })
          .eq("id", keyRequest.id);

        if (updateError) {
          console.error('Error updating is_covered:', updateError);
        }

        // Mettre à jour le trust score (+5 points)
        // D'abord récupérer le trust score actuel
        const { data: currentProfile } = await supabase
          .from("profiles")
          .select("trust_score")
          .eq("id", user.id)
          .single();

        const newTrustScore = ((currentProfile as any)?.trust_score || 0) + 5;
        
        const { error: trustScoreError } = await (supabase as any)
          .from("profiles")
          .update({ 
            trust_score: newTrustScore
          })
          .eq("id", user.id);

        if (trustScoreError) {
          console.error('Error updating trust score:', trustScoreError);
        }

      }

      // Recharger les coverage items
      if (userType === 'creator') {
        await fetchCoverageItems();
      }
    } catch (error) {
      console.error('Error saving matching content:', error);
    }
  };

  const hasMatchingApprovedGame = (contentTags: string[]): boolean => {
    if (!contentTags || contentTags.length === 0) return false;
    
    return approvedGames.some(game => {
      if (!game.hashtag) return false;
      
      // Retirer le # du hashtag du jeu pour la comparaison
      const gameHashtag = game.hashtag.replace('#', '').toLowerCase();
      
      // Vérifier si au moins un tag du contenu correspond au hashtag du jeu
      return contentTags.some(contentTag => {
        const cleanContentTag = contentTag.toLowerCase();
        return cleanContentTag.includes(gameHashtag) || gameHashtag.includes(cleanContentTag);
      });
    });
  };


  if (isProfileLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Loading your profile...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si l'utilisateur est un player, afficher un message d'information
  if (userType === 'player') {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <Gamepad2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h1 className="text-2xl font-bold mb-2">Coverage</h1>
            <p className="text-muted-foreground mb-4">
              This feature is only available for content creators and game developers.
            </p>
            <p className="text-sm text-muted-foreground">
              As a player, you can browse games and request keys from developers.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Coverage</h1>
          <p className="text-muted-foreground">
            {userType === 'creator' 
              ? 'Manage and view your content across different platforms'
              : 'View coverage of your games by content creators'
            }
          </p>
        </div>

        <div className="space-y-6">
          {/* Connection Status and Refresh - Only for creators */}
          {userType === 'creator' && (
            <div className="space-y-4">
              {(userProfile?.youtube_connected === true || userProfile?.twitch_connected === true) && (
                <div>
                  <Button 
                    onClick={fetchContent} 
                    disabled={isContentLoading}
                    className="w-full md:w-auto"
                  >
                    {isContentLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Loading Content...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh Content
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Game Filter - Only for developers */}
          {userType === 'developer' && developerGames.length > 0 && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Filter by Game</h3>
              <Select value={selectedGameId} onValueChange={setSelectedGameId}>
                <SelectTrigger className="w-full md:w-80">
                  <SelectValue placeholder="Select a game" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    All Games ({coverageItems.length})
                  </SelectItem>
                  {developerGames.map((game) => {
                    const gameCoverageCount = coverageItems.filter(item => item.game_id === game.id).length;
                    return (
                      <SelectItem key={game.id} value={game.id}>
                        {game.title} ({gameCoverageCount})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </Card>
          )}

          {/* Coverage Content */}
          {userType === 'creator' ? (
            <CoverageContent
              coverageItems={getDisplayCoverageItems()}
              userProfile={userProfile}
              isLoading={isContentLoading}
            />
          ) : userType === 'developer' ? (
            <div className="space-y-4">
              {getDisplayCoverageItems().length > 0 && !isContentLoading ? (
                <>
                  <h3 className="text-lg font-semibold">Game Coverage</h3>
                  {getDisplayCoverageItems().map((item) => (
                    <Card key={item.id} className="bg-muted/20 border dark:bg-green-950/20 dark:border-green-800 hover:bg-muted/50 p-4 rounded-lg shadow-sm text-card-foreground transition-colors">
                      <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-shrink-0 cursor-pointer w-full md:w-auto h-28 overflow-hidden rounded-lg" onClick={() => window.open(item.video_url, '_blank')}>
                          <img
                            src={item.thumbnail_url}
                            alt={item.title}
                            className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                          />
                          <div className="absolute top-1 left-1">
                            <Badge className={item.platform === 'youtube' ? 'bg-red-500 text-white' : 'bg-purple-500 text-white'}>
                              {item.platform === 'youtube' ? '📺' : '🎮'}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 
                            className="font-semibold truncate mb-1 cursor-pointer hover:text-primary transition-colors" 
                            onClick={() => window.open(item.video_url, '_blank')}
                          >
                            {item.title}
                          </h4>

                          {item.game && (
                            <div className="text-sm text-muted-foreground mb-1">
                              <span className="font-medium">Game:</span> {item.game.title}
                            </div>
                          )}
                          {item.creator && (
                            <div className="text-sm text-muted-foreground mb-2">
                              <span className="font-medium">Creator:</span> {item.creator.studio_name || item.creator.display_name}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                            <div className="flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              {item.views >= 1000000 ? (item.views / 1000000).toFixed(1) + 'M' : item.views >= 1000 ? (item.views / 1000).toFixed(1) + 'K' : item.views} views
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {new Date(item.posted_date).toLocaleDateString()}
                            </div>
                          </div>

                          {item.platform === 'youtube' && item.tags && item.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {item.tags.map((tag, index) => (
                                <Badge key={index} variant="secondary" className="text-[10px] px-1 py-0">
                                  #{tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </>
              ) : !isContentLoading ? (
                <Card className="p-8 text-center">
                  <div className="text-muted-foreground">
                    <Play className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No coverage found</p>
                    <p className="text-sm">
                      {selectedGameId === 'all' 
                        ? 'No content creators have covered your games yet.'
                        : 'No content creators have covered this game yet.'
                      }
                    </p>
                  </div>
                </Card>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </Layout>
  );
}

export default Coverage;
